import type { PlanTier, TierLimits } from '@dometry/plans';
import type { HttpClient } from '../http/HttpClient.ts';

export type PlanSource = 'free' | 'grant' | 'stripe' | 'internal';

export interface PlanUsage {
  organizations: number;
  people: number;
  storageBytes: number;
  openItems: number;
  agents: number;
}

export interface PlanPerson {
  userId: string;
  name: string;
  email: string;
  orgs: { slug: string; name: string; role: string; paused: boolean }[];
  joinedAt: string;
  /** Paused in at least one of your organizations (restoring covers all). */
  paused: boolean;
}

export interface PlanInvited {
  email: string;
  orgs: { slug: string; name: string }[];
  expiresAt: string;
}

export interface PlanOverview {
  plan: {
    tier: PlanTier;
    source: PlanSource;
    endsAt: string | null;
    /** A grant that ended: Free applies now. */
    lapsed: boolean;
    limits: TierLimits;
    peopleGraceUntil: string | null;
  };
  usage: PlanUsage;
  /** Over the people limit: how many, and how many it allows; null when it fits. */
  over: { people: number; allowed: number } | null;
  /** Whether plan limits are switched on (off: nothing is refused). */
  enforced: boolean;
  people: PlanPerson[];
  invited: PlanInvited[];
}

/** People on the plan that covers an organization (its owner's), for the invite form. */
export interface OrgPeopleRoom {
  tier: PlanTier;
  used: number;
  allowed: number | null;
  ownerName: string;
  /** Whether it's your own plan. */
  yours: boolean;
  /** Whether plan limits are switched on (off: nothing is refused). */
  enforced: boolean;
}

/** Your plan (/me/plan) and an organization's places left. */
export class PlansApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  overview(): Promise<PlanOverview> {
    return this.#http.get('/me/plan');
  }

  pause(userId: string): Promise<void> {
    return this.#http.post(`/me/plan/people/${encodeURIComponent(userId)}/pause`);
  }

  resume(userId: string): Promise<void> {
    return this.#http.post(`/me/plan/people/${encodeURIComponent(userId)}/resume`);
  }

  /** Restore `userId` in place of `outgoingUserId` (paused instead), in one step. */
  swap(userId: string, outgoingUserId: string): Promise<void> {
    return this.#http.post(`/me/plan/people/${encodeURIComponent(userId)}/swap`, {
      outgoingUserId,
    });
  }

  orgPeople(org: string): Promise<OrgPeopleRoom> {
    return this.#http.get(`/orgs/${encodeURIComponent(org)}/plan/people`);
  }
}
